import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '../../components/Common/Box';
import TradingFeedback from './TradingFeedback';
import NiaPortfolioHistory from './NiaPortfolioHistory';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

interface MobileNiaPortfolioProps {
    aiPortfolio: any;
    totalInvested: number;
    aiPositions: any[];
    aiDecisions: any[];
    portfolioHistory: any[];
    isAdmin: boolean;
    isPro: boolean;
    cycleLoading: boolean;
    setActiveModal: (modal: 'reset' | 'adjust' | 'run' | null) => void;
    setAdjustForm: (form: { amount: string, reason: string }) => void;
    handleCreateAiPortfolio: () => void;
    aiLoading: boolean;
}

const MobileDecisionCard = ({ decision }: { decision: any }) => {
    const [expanded, setExpanded] = useState(false);

    // Parse actions if any
    let actions: any[] = [];
    try {
        if (decision.proposed_actions) {
            actions = typeof decision.proposed_actions === 'string'
                ? JSON.parse(decision.proposed_actions)
                : decision.proposed_actions;
        }
    } catch (e) {
        console.error("Error parsing actions", e);
    }

    const decisionDate = new Date(decision.created_at || decision.date);
    const dateStr = decisionDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    const timeStr = decisionDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    // Identify primary action for badge
    let primaryAction = 'ANALYSE';
    let badgeColor = '#64748b'; // default slate
    let badgeBg = 'rgba(100, 116, 139, 0.1)';

    if (actions.length > 0) {
        primaryAction = actions[0].action === 'BUY' ? 'ACHAT' : 'VENTE';
        badgeColor = actions[0].action === 'BUY' ? '#10b981' : '#ef4444';
        badgeBg = actions[0].action === 'BUY' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';
        if (actions.length > 1) primaryAction += ` +${actions.length - 1}`;
    }

    return (
        <Box>
            <div style={{ padding: '16px' }} onClick={() => setExpanded(!expanded)}>
                {/* Header (Always visible) */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: badgeColor,
                            backgroundColor: badgeBg,
                            padding: '4px 8px',
                            borderRadius: '6px'
                        }}>
                            {primaryAction}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                            {dateStr} à {timeStr}
                        </span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>ID: {decision.id}</span>
                </div>

                {/* Main reason summary */}
                {!expanded && (
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.5' }}>
                        {decision.ai_reasoning}
                    </div>
                )}

                {/* Expanded Details */}
                {expanded && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border-color)', animation: 'fadeIn 0.2s ease-in-out' }}>
                        {actions.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                                {actions.map((a, idx) => (
                                    <div key={idx} style={{ padding: '10px', backgroundColor: 'var(--bg-main)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: a.action === 'BUY' ? '#10b981' : '#ef4444', marginBottom: '2px' }}>
                                                {a.action === 'BUY' ? 'ACHAT' : 'VENTE'} {a.symbol}
                                            </div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Qté: {a.quantity}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ fontSize: '13px', color: 'var(--text-main)', lineHeight: '1.6', marginBottom: '12px' }}>
                            <strong>Raisonnement IA :</strong><br />
                            {decision.ai_reasoning}
                        </div>

                        {decision.execution_result && (
                            <div style={{ fontSize: '12px', color: 'var(--text-main)', backgroundColor: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.3)', padding: '10px', borderRadius: '8px' }}>
                                {decision.execution_result.split('\n').map((line: string, i: number) => (
                                    <div key={i} style={{ marginBottom: 4 }}>• {line}</div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={{ padding: '0 16px 16px 16px' }}>
                <TradingFeedback
                    decisionId={decision.id}
                    initialLikes={decision.likes_count || 0}
                    initialHasLiked={decision.has_liked || false}
                    initialCommentsCount={decision.comments_count || 0}
                />
            </div>

            <div style={{ textAlign: 'center', padding: '8px 0', borderTop: '1px solid var(--border-color)', color: '#3b82f6', fontSize: '13px', fontWeight: '600' }} onClick={() => setExpanded(!expanded)}>
                {expanded ? '▲ Masquer les détails' : '▼ Voir les détails'}
            </div>
        </Box>
    );
};


const MobileNiaPortfolio: React.FC<MobileNiaPortfolioProps> = ({
    aiPortfolio,
    totalInvested,
    aiPositions,
    aiDecisions,
    portfolioHistory,
    isAdmin,
    isPro,
    cycleLoading,
    setActiveModal,
    setAdjustForm,
    handleCreateAiPortfolio,
    aiLoading
}) => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'positions' | 'decisions'>('positions');
    const [decisions, setDecisions] = useState<any[]>(aiDecisions);
    const [decisionsTotal, setDecisionsTotal] = useState<number>(aiDecisions.length);
    const [decisionsLoading, setDecisionsLoading] = useState(false);

    React.useEffect(() => {
        setDecisions(aiDecisions);
    }, [aiDecisions]);

    React.useEffect(() => {
        if (!token || !aiPortfolio) return;
        fetch(`${API_URL}/api/ai-portfolio/global/decisions?offset=0&limit=1`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()).then(d => setDecisionsTotal(d.total)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiPortfolio]);

    const loadMoreDecisions = useCallback(async () => {
        if (!token || decisionsLoading) return;
        setDecisionsLoading(true);
        try {
            const res = await fetch(
                `${API_URL}/api/ai-portfolio/global/decisions?offset=${decisions.length}&limit=15`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) return;
            const data = await res.json();
            setDecisions(prev => [...prev, ...data.decisions]);
            setDecisionsTotal(data.total);
        } catch (e) {
            console.error(e);
        } finally {
            setDecisionsLoading(false);
        }
    }, [token, decisions.length, decisionsLoading]);

    if (aiLoading) {
        return <div style={{ textAlign: 'center', padding: 40 }}><div className="loading-spinner" /><p>Chargement du portefeuille...</p></div>;
    }

    if (!aiPortfolio) {
        return (
            <div style={{ textAlign: 'center', padding: 40 }}>
                <h3>🤖 Aucun portefeuille AI actif</h3>
                <button
                    onClick={handleCreateAiPortfolio}
                    style={{ backgroundColor: '#7c3aed', color: 'white', padding: '14px 24px', borderRadius: '12px', border: 'none', fontWeight: 'bold', width: '100%', marginTop: '20px' }}>
                    Créer le Portefeuille de Nia
                </button>
            </div>
        );
    }

    const currentVal = aiPortfolio.current_value || (aiPortfolio.cash_available || 0) + (aiPortfolio.cash_settling || 0) + aiPositions.reduce((acc, p) => acc + (p.current_value || 0), 0);
    const absolutePerf = aiPortfolio.performance_abs ?? (currentVal - (totalInvested || 1000000));
    const percentPerf = aiPortfolio.performance_pct ?? ((absolutePerf / (totalInvested || 1000000)) * 100);
    const startDate = aiPortfolio.created_at ? new Date(aiPortfolio.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '16 fév. 2026';

    return (
        <div style={{ paddingBottom: '30px' }}>
            {/* --- PREMIUM MOBILE HEADER --- */}
            <div style={{ padding: '20px', background: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '15px' }}>
                    <div style={{ padding: '10px 8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>📅 Début</div>
                        <div style={{ fontSize: '11px', fontWeight: '700' }}>{startDate}</div>
                    </div>
                    <div style={{ padding: '10px 8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>💰 Investi</div>
                        <div style={{ fontSize: '11px', fontWeight: '700' }}>{(totalInvested || 1000000).toLocaleString()}</div>
                    </div>
                    <div style={{ padding: '10px 8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: '#ef4444', textTransform: 'uppercase', marginBottom: '4px' }}>💸 Frais</div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#ef4444' }}>-{aiPortfolio?.total_fees?.toLocaleString() || '0'}</div>
                    </div>
                </div>

                <div style={{ textAlign: 'center', padding: '15px 0' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', transition: 'all 0.2s', marginBottom: '5px' }}>VALEUR ACTUELLE</div>
                    <div style={{ fontSize: '30px', fontWeight: '800', color: '#a78bfa' }}>
                        {currentVal.toLocaleString()} <span style={{ fontSize: '16px' }}>FCFA</span>
                    </div>

                    <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '800', 
                        color: 'white', 
                        backgroundColor: absolutePerf >= 0 ? '#10b981' : '#ef4444', 
                        padding: '4px 12px', 
                        borderRadius: '20px', 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginTop: '10px'
                    }}>
                        {absolutePerf >= 0 ? '▲' : '▼'} {percentPerf.toFixed(2)}%
                        <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.9 }}>
                            ({absolutePerf >= 0 ? '+' : ''}{Math.round(absolutePerf).toLocaleString()})
                        </span>
                    </div>
                </div>
            </div>

            {/* --- ADMIN CONSOLE (If Admin) --- */}
            {isAdmin && (
                <div style={{ padding: '16px 20px', background: 'rgba(239, 68, 68, 0.05)', borderBottom: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#ef4444', marginBottom: '12px' }}>🛡️ ADMIN ACTIONS</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                        <button onClick={() => setActiveModal('run')} disabled={cycleLoading} style={{ padding: '12px', borderRadius: '10px', border: 'none', background: '#7c3aed', color: 'white', fontWeight: 'bold' }}>
                            ⚡ Lancer Cycle AI
                        </button>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <button onClick={() => { setAdjustForm({ amount: '', reason: '' }); setActiveModal('adjust'); }} style={{ padding: '10px', borderRadius: '10px', border: 'none', background: '#10b981', color: 'white', fontWeight: 'bold', fontSize: '12px' }}>
                                💰 Crédit/Débit
                            </button>
                            <button onClick={() => setActiveModal('reset')} style={{ padding: '10px', borderRadius: '10px', border: 'none', background: '#ef4444', color: 'white', fontWeight: 'bold', fontSize: '12px' }}>
                                ⚠️ Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {portfolioHistory.length >= 2 && (
                <NiaPortfolioHistory
                    history={portfolioHistory}
                    totalInvested={totalInvested}
                    isMobile={true}
                />
            )}

            {/* --- SEGMENTED CONTROL --- */}
            <div style={{ padding: '16px 20px', position: 'sticky', top: '0', background: 'var(--bg-main)', zIndex: 10, borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', background: 'var(--bg-input)', borderRadius: '12px', padding: '4px' }}>
                    <button
                        onClick={() => setActiveTab('positions')}
                        style={{
                            flex: 1, padding: '12px 0', borderRadius: '10px', fontSize: '14px', fontWeight: '600',
                            background: activeTab === 'positions' ? '#7c3aed' : 'transparent',
                            color: activeTab === 'positions' ? '#ffffff' : 'var(--text-muted)',
                            border: 'none', boxShadow: activeTab === 'positions' ? '0 2px 4px rgba(124, 58, 237, 0.3)' : 'none',
                            transition: 'all 0.2s', touchAction: 'manipulation'
                        }}
                    >
                        Positions ({aiPositions.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('decisions')}
                        style={{
                            flex: 1, padding: '12px 0', borderRadius: '10px', fontSize: '14px', fontWeight: '600',
                            background: activeTab === 'decisions' ? '#7c3aed' : 'transparent',
                            color: activeTab === 'decisions' ? '#ffffff' : 'var(--text-muted)',
                            border: 'none', boxShadow: activeTab === 'decisions' ? '0 2px 4px rgba(124, 58, 237, 0.3)' : 'none',
                            transition: 'all 0.2s', touchAction: 'manipulation'
                        }}
                    >
                        Décisions IA
                    </button>
                </div>
            </div>

            {/* --- TABS BENEATH --- */}
            <div style={{ padding: '20px' }}>
                {/* --- POSITIONS TAB --- */}
                {activeTab === 'positions' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* CASH CARD */}
                        <Box>
                            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ fontSize: '24px' }}>💵</div>
                                    <div>
                                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-heading)' }}>Liquidités (Cash)</div>
                                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Cash Dispo. & En Règlement</div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '18px', fontWeight: '800', color: '#10b981' }}>
                                        {(aiPortfolio.cash_available + aiPortfolio.cash_settling)?.toLocaleString() || 0}
                                    </div>
                                </div>
                            </div>
                        </Box>

                        <div style={{ position: 'relative', width: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {aiPositions.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                                        Aucune action en portefeuille.
                                    </div>
                                ) : aiPositions.map((p: any) => {
                                    const pnl = (p.current_value || 0) - (p.quantity * p.average_buy_price);
                                    const pnlPct = p.quantity > 0 ? ((p.current_value || 0) / (p.quantity * p.average_buy_price) - 1) * 100 : 0;
                                    const today = new Date(); today.setHours(0, 0, 0, 0);
                                    const isDelivered = !p.settlement_date || new Date(p.settlement_date) <= today;

                                    return (
                                        <Box key={p.stock_symbol}>
                                            <div style={{ padding: '16px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                    <div>
                                                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-heading)' }}>{p.stock_symbol}</div>
                                                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Qté: {isPro ? p.quantity : '•••'}</div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: p.current_value ? 'var(--text-heading)' : 'var(--text-muted)' }}>
                                                            {isPro && p.current_value ? p.current_value.toLocaleString() : '••••'}
                                                        </div>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: pnl >= 0 ? '#10b981' : '#ef4444', marginTop: '4px' }}>
                                                            {isPro ? `${pnl > 0 ? '+' : ''}${pnl.toLocaleString()} (${pnlPct.toFixed(2)}%)` : '••%'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ background: 'var(--bg-main)', borderRadius: '8px', padding: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                                                    <div>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>PRU</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-main)' }}>{isPro ? p.average_buy_price?.toLocaleString() : '••••'}</div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Cours Actuel</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-main)' }}>{isPro ? (p.current_price?.toLocaleString() || '-') : '••••'}</div>
                                                    </div>
                                                    <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)', paddingTop: '8px', marginTop: '4px' }}>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Statut (J+3)</span>
                                                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: isDelivered ? '#10b981' : '#f59e0b' }}>
                                                            {isPro ? (isDelivered ? '✅ Livré' : `⏳ En attente (${new Date(p.settlement_date).toLocaleDateString('fr-FR')})`) : '—'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Box>
                                    );
                                })}
                            </div>

                            {!isPro && (
                                <div className="paywall-overlay">
                                    <div className="paywall-icon-circle" style={{ width: '40px', height: '40px', marginBottom: '8px' }}>
                                        <i className="material-icons" style={{ fontSize: '20px', color: '#7c3aed' }}>lock</i>
                                    </div>
                                    <h4 className="paywall-title" style={{ fontSize: '14px', marginBottom: '4px' }}>
                                        Composition verrouillée
                                    </h4>
                                    <p className="paywall-desc" style={{ fontSize: '12px', maxWidth: '280px', marginBottom: '12px' }}>
                                        Consultez les titres détenus, les quantités et les performances du portefeuille géré par le modèle de momentum.
                                    </p>
                                    <button onClick={() => navigate('/upgrade')} className="paywall-btn" style={{ padding: '8px 16px', fontSize: '12px' }}>
                                        Débloquer maintenant
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* --- DECISIONS TAB --- */}
                {activeTab === 'decisions' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>JOURNAL IA</span>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{decisions.length} / {decisionsTotal}</span>
                        </div>

                        <div style={{ position: 'relative', width: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {decisions.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                                        Aucun journal de décisions.
                                    </div>
                                ) : (
                                    (isPro ? decisions : decisions.slice(0, 3)).map(d => (
                                        <MobileDecisionCard key={d.id} decision={d} />
                                    ))
                                )}

                                {isPro && decisions.length < decisionsTotal && (
                                    <button
                                        onClick={loadMoreDecisions}
                                        disabled={decisionsLoading}
                                        style={{
                                            padding: '14px',
                                            borderRadius: 12,
                                            border: '1px solid var(--border-color)',
                                            background: 'var(--bg-input)',
                                            color: 'var(--text-main)',
                                            fontWeight: 700,
                                            fontSize: 14,
                                            cursor: decisionsLoading ? 'not-allowed' : 'pointer',
                                            opacity: decisionsLoading ? 0.6 : 1,
                                            width: '100%',
                                        }}
                                    >
                                        {decisionsLoading
                                            ? '⏳ Chargement...'
                                            : `📜 Charger ${Math.min(15, decisionsTotal - decisions.length)} de plus (${decisionsTotal - decisions.length} restantes)`
                                        }
                                    </button>
                                )}
                            </div>

                            {!isPro && (
                                <div className="paywall-overlay">
                                    <div className="paywall-icon-circle" style={{ width: '40px', height: '40px', marginBottom: '8px' }}>
                                        <i className="material-icons" style={{ fontSize: '20px', color: '#7c3aed' }}>lock</i>
                                    </div>
                                    <h4 className="paywall-title" style={{ fontSize: '14px', marginBottom: '4px' }}>
                                        Journal de Décisions verrouillé
                                    </h4>
                                    <p className="paywall-desc" style={{ fontSize: '12px', maxWidth: '280px', marginBottom: '12px' }}>
                                        Accédez au journal des décisions d'achat et de vente de l'IA, avec les signaux techniques et le raisonnement motivant chaque opération.
                                    </p>
                                    <button onClick={() => navigate('/upgrade')} className="paywall-btn" style={{ padding: '8px 16px', fontSize: '12px' }}>
                                        Débloquer maintenant
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileNiaPortfolio;
